/**
 * Plantilla y validación del Excel de Asignación de Relojes Checadores.
 *
 * Sin React ni antd a propósito: así el workbook y el validador se pueden generar
 * y revisar con un `node script.js`, sin levantar la app.
 *
 * La hoja principal se descarga YA POBLADA con las asignaciones actuales (con su
 * ID y su Estado): el flujo es descargar → editar → subir, sin copiar y pegar.
 *
 * Lo que el Excel puede hacer: crear asignaciones nuevas y habilitar/deshabilitar
 * las que ya existen. Lo que NO: cambiarle el empleado, el reloj o el usuario a una
 * asignación existente — eso se hace en la pantalla, y aquí sale como error con el
 * motivo, para que nadie crea que se aplicó.
 */

import * as XLSX from "xlsx";
import { encodeId, decodeId } from "../../../../Components/idEncoder";
import {
  ESTADO_NOMBRE,
  claveSerieNumero,
  construirIndiceEmpleados,
  construirIndiceEtiquetado,
  hojaDeCatalogo,
  hojaDeFilas,
  hojaDeInstrucciones,
  nombreEmpleado,
  parsearEstado,
  resolverEmpleado,
  resolverEtiquetado,
  texto,
} from "../../../../Components/importarAsignaciones.utils";

export const COLUMNAS = [
  "ID",
  "Número Empleado",
  "Empleado",
  "Reloj Checador",
  "Usuario Reloj",
  "Estado",
];

const ANCHOS = [16, 16, 34, 40, 14, 16];

export const NOMBRE_PLANTILLA = "plantillaAsignacionRelojes.xlsx";

// Misma forma que la columna "Reloj Checador" de la tabla de Asignación de Relojes,
// para que lo que se ve en la pantalla y lo que se ve en el Excel sean lo mismo.
export const etiquetaReloj = (reloj) => {
  if (!reloj) return "";
  return [claveSerieNumero(reloj), texto(reloj.nombre), texto(reloj.descripcion)]
    .filter(Boolean)
    .join(" - ");
};

// El sn identifica el reloj FÍSICO, que puede estar dado de alta como varios
// RelojChecador (uno por empresa). Los duplicados de usuario se validan contra
// toda esa familia, no contra un solo registro — igual que agregarAsignacionReloj.
const construirFamilias = (relojes) => {
  const porSn = new Map();

  for (const reloj of relojes) {
    const sn = texto(reloj.sn).toUpperCase();
    const clave = sn || `id:${String(reloj._id)}`;
    if (!porSn.has(clave)) porSn.set(clave, new Set());
    porSn.get(clave).add(String(reloj._id));
  }

  const familiaPorRelojId = new Map();
  for (const familia of porSn.values()) {
    for (const relojId of familia) familiaPorRelojId.set(relojId, familia);
  }
  return familiaPorRelojId;
};

export const prepararCatalogos = (resData) => {
  const asignaciones = resData.asignaciones || [];
  const empleados = resData.empleados || [];
  const relojes = resData.relojes || [];

  const asignacionPorId = new Map(asignaciones.map((a) => [String(a._id), a]));

  const porEmpleado = new Map();
  for (const asignacion of asignaciones) {
    const clave = String(asignacion.empleadoId);
    if (!porEmpleado.has(clave)) porEmpleado.set(clave, []);
    porEmpleado.get(clave).push(asignacion);
  }

  // Usuarios ya ocupados, por reloj. Sólo las activas: una deshabilitada ya no
  // tiene usuario en el dispositivo.
  const activasPorReloj = new Map();
  for (const asignacion of asignaciones) {
    if (Number(asignacion.estado) !== 1) continue;
    const clave = String(asignacion.relojChecadorId);
    if (!activasPorReloj.has(clave)) activasPorReloj.set(clave, []);
    activasPorReloj.get(clave).push(asignacion);
  }

  return {
    asignaciones,
    empleados,
    relojes,
    asignacionPorId,
    porEmpleado,
    activasPorReloj,
    empleadoPorId: new Map(empleados.map((e) => [String(e._id), e])),
    relojPorId: new Map(relojes.map((r) => [String(r._id), r])),
    indiceEmpleados: construirIndiceEmpleados(empleados),
    indiceRelojes: construirIndiceEtiquetado(relojes, etiquetaReloj),
    familiaPorRelojId: construirFamilias(relojes),
  };
};

// ─── Plantilla ───────────────────────────────────────────────────────────────

const INSTRUCCIONES_HOJA = [
  "IMPORTAR / ACTUALIZAR ASIGNACIONES DE RELOJES CHECADORES",
  "",
  'La hoja "Asignaciones" ya viene con todas tus asignaciones actuales. Edítala y vuelve a subir este mismo archivo.',
  "",
  "PARA DESHABILITAR: cambia la columna Estado de esa fila a Deshabilitado.",
  "   Al deshabilitar, el empleado tambien se BORRA del reloj checador (usuario, huellas y rostro).",
  "",
  "PARA HABILITAR: cambia la columna Estado a Activo.",
  "   Al habilitar se le vuelven a mandar al reloj sus credenciales (usuario, huellas y rostro).",
  "",
  "PARA CREAR UNA ASIGNACION NUEVA: agrega una fila al final, DEJA LA COLUMNA ID VACIA y llena",
  "   Numero Empleado, Reloj Checador y Estado = Activo.",
  "   Si dejas Usuario Reloj vacio, el sistema le pone el siguiente numero libre de ese reloj.",
  "",
  "LO QUE NO SE PUEDE HACER DESDE AQUI: cambiarle el empleado, el reloj o el usuario a una",
  "   asignacion que ya existe (una fila que ya trae ID). Eso se hace en la pantalla de",
  "   Asignacion de Relojes. Si lo intentas, la fila sale reportada en errores.xlsx.",
  "",
  "OJO: si a un empleado que ya esta activo en un reloj le pones una fila NUEVA con otro",
  "   Usuario Reloj, se deshabilita la asignacion anterior (borrandola del reloj) y se crea la",
  "   nueva. Es para que no queden dos usuarios de la misma persona checando en el mismo reloj.",
  "",
  "Borrar una fila del archivo NO borra nada: las filas que no vengan se quedan como estan.",
  "",
  "El alta y la baja en el reloj fisico NO son instantaneas: se encolan y el dispositivo se las",
  "lleva poco a poco cada vez que se conecta. Puedes verificar como va en Relojes Checadores ->",
  "Informacion del Reloj Checador.",
  "",
  'Las hojas "Empleados" y "Relojes" son de consulta: te dicen que valores son validos.',
];

export const construirPlantilla = (catalogos) => {
  const { asignaciones = [], empleados = [], relojes = [] } = catalogos || {};
  const empleadoPorId = catalogos?.empleadoPorId || new Map();
  const relojPorId = catalogos?.relojPorId || new Map();

  const filas = asignaciones
    .map((asignacion) => {
      const empleado = empleadoPorId.get(String(asignacion.empleadoId));
      const reloj = relojPorId.get(String(asignacion.relojChecadorId));
      return {
        ID: encodeId(String(asignacion._id)),
        "Número Empleado": claveSerieNumero(empleado),
        Empleado: nombreEmpleado(empleado),
        "Reloj Checador": etiquetaReloj(reloj),
        "Usuario Reloj": texto(asignacion.usuarioRelojChecador),
        Estado: ESTADO_NOMBRE[Number(asignacion.estado)] || "",
      };
    })
    .sort(
      (a, b) =>
        a["Reloj Checador"].localeCompare(b["Reloj Checador"]) ||
        a.Empleado.localeCompare(b.Empleado),
    );

  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, hojaDeFilas(COLUMNAS, filas, ANCHOS), "Asignaciones");

  XLSX.utils.book_append_sheet(
    wb,
    hojaDeCatalogo(
      ["Número", "Nombre", "Estado"],
      empleados
        .map((empleado) => ({
          Número: claveSerieNumero(empleado),
          Nombre: nombreEmpleado(empleado),
          Estado: ESTADO_NOMBRE[Number(empleado.estado)] || "",
        }))
        .sort((a, b) => a.Nombre.localeCompare(b.Nombre)),
      [16, 40, 16],
    ),
    "Empleados",
  );

  XLSX.utils.book_append_sheet(
    wb,
    hojaDeCatalogo(
      ["Clave", "Nombre", "Descripción", "SN", "Estado"],
      relojes.map((reloj) => ({
        Clave: claveSerieNumero(reloj),
        Nombre: texto(reloj.nombre),
        Descripción: texto(reloj.descripcion),
        SN: texto(reloj.sn),
        Estado: ESTADO_NOMBRE[Number(reloj.estado)] || "",
      })),
      [14, 28, 32, 22, 16],
    ),
    "Relojes",
  );

  XLSX.utils.book_append_sheet(wb, hojaDeInstrucciones(INSTRUCCIONES_HOJA), "Instrucciones");

  return wb;
};

// ─── Validación ──────────────────────────────────────────────────────────────

const numeroDeFila = (fila) => (fila["__rowNum__"] ?? 0) + 1;

// Los apartados de PIN y de (empleado, reloj) del propio archivo se cuelgan del
// contexto, que ImportadorExcelGenerico comparte entre todas las filas de la
// misma subida.
const apartados = (contexto, nombre) => {
  if (!contexto[nombre]) contexto[nombre] = new Map();
  return contexto[nombre];
};

/**
 * Pasada previa sobre la hoja, antes de validar renglón por renglón: junta los ids
 * de las asignaciones que el propio archivo va a deshabilitar.
 *
 * Sólo se usa para dar un mensaje entendible cuando alguien intenta reusar en el
 * mismo archivo un usuario que ese archivo libera. Reusarlo NO se permite: el
 * alta y la baja pueden viajar en bloques distintos, y si el alta llega primero
 * la baja terminaría borrando del reloj el usuario que se acababa de crear.
 */
export const prepararContextoArchivo = (hojas, catalogos) => {
  const idsDesactivados = new Set();

  for (const fila of hojas?.[0]?.data || []) {
    const idEncoded = texto(fila["ID"]);
    if (!idEncoded) continue;

    const decodificado = decodeId(idEncoded);
    const asignacion = decodificado
      ? catalogos.asignacionPorId.get(String(decodificado))
      : null;

    if (!asignacion) continue;
    if (Number(asignacion.estado) !== 1) continue;
    if (parsearEstado(fila["Estado"]) === 2) idsDesactivados.add(String(asignacion._id));
  }

  return { idsDesactivados };
};

export const validarFila = (fila, catalogos, _indice, contexto) => {
  const idEncoded = texto(fila["ID"]);
  const numeroTexto = texto(fila["Número Empleado"]);
  const nombreTexto = texto(fila["Empleado"]);
  const relojTexto = texto(fila["Reloj Checador"]);
  const pinTexto = texto(fila["Usuario Reloj"]);
  const estadoTexto = texto(fila["Estado"]);

  const errores = [];

  const estadoDestino = parsearEstado(estadoTexto);
  if (!estadoTexto) {
    errores.push({ campo: "Estado", motivo: 'está vacío (pon "Activo" o "Deshabilitado")' });
  } else if (!estadoDestino) {
    errores.push({
      campo: "Estado",
      motivo: `"${estadoTexto}" no se entiende; usa "Activo" o "Deshabilitado"`,
    });
  }

  const resEmpleado = resolverEmpleado(catalogos.indiceEmpleados, { numeroTexto, nombreTexto });
  if (resEmpleado.errores) errores.push(...resEmpleado.errores);

  const resReloj = resolverEtiquetado(catalogos.indiceRelojes, relojTexto, {
    campo: "Reloj Checador",
    hoja: "Relojes",
    ejemplo: "RC - 1",
  });
  if (resReloj.errores) errores.push(...resReloj.errores);

  if (pinTexto && !/^\d+$/.test(pinTexto)) {
    errores.push({ campo: "Usuario Reloj", motivo: `"${pinTexto}" debe ser un número` });
  }

  if (errores.length > 0) return { ok: false, errores };

  const empleado = resEmpleado.empleado;
  const reloj = resReloj.item;

  const datos = {
    empleadoId: String(empleado._id),
    relojChecadorId: String(reloj._id),
    usuarioRelojChecador: pinTexto,
    // Sólo para la vista previa (el backend los ignora).
    empleadoNumero: claveSerieNumero(empleado),
    empleadoNombre: nombreEmpleado(empleado),
    relojChecadorNombre: etiquetaReloj(reloj),
    estadoNombre: ESTADO_NOMBRE[estadoDestino],
  };

  // ── Fila con ID: sólo puede prender o apagar ────────────────────────────────
  if (idEncoded) {
    const decodificado = decodeId(idEncoded);
    const asignacion = decodificado
      ? catalogos.asignacionPorId.get(String(decodificado))
      : null;

    if (!asignacion) {
      return {
        ok: false,
        errores: [{ campo: "ID", motivo: `"${idEncoded}" no corresponde a ninguna asignación` }],
      };
    }

    const idsDelArchivo = apartados(contexto, "idsUsados");
    if (idsDelArchivo.has(idEncoded)) {
      return {
        ok: false,
        errores: [
          { campo: "ID", motivo: `repetido (también está en la fila ${idsDelArchivo.get(idEncoded)})` },
        ],
      };
    }
    idsDelArchivo.set(idEncoded, numeroDeFila(fila));

    const noEditables = [];
    if (String(asignacion.empleadoId) !== String(empleado._id)) noEditables.push("Empleado");
    if (String(asignacion.relojChecadorId) !== String(reloj._id)) noEditables.push("Reloj Checador");
    if (pinTexto && texto(asignacion.usuarioRelojChecador) !== pinTexto) {
      noEditables.push("Usuario Reloj");
    }

    if (noEditables.length > 0) {
      return {
        ok: false,
        errores: noEditables.map((campo) => ({
          campo,
          motivo:
            "desde el Excel sólo se puede habilitar o deshabilitar; este cambio se hace en la pantalla de Asignación de Relojes",
        })),
      };
    }

    const estadoActual = Number(asignacion.estado);
    const valoresAnteriores = { estadoNombre: ESTADO_NOMBRE[estadoActual] || "" };

    if (estadoActual === estadoDestino) {
      return {
        ok: true,
        accion: "sinCambios",
        idExistente: String(asignacion._id),
        datos: { ...datos, operacion: "sinCambios" },
        valoresAnteriores,
      };
    }

    return {
      ok: true,
      accion: "editar",
      idExistente: String(asignacion._id),
      datos: {
        ...datos,
        usuarioRelojChecador: texto(asignacion.usuarioRelojChecador),
        operacion: estadoDestino === 1 ? "habilitar" : "deshabilitar",
      },
      cambios: ["estadoNombre"],
      valoresAnteriores,
    };
  }

  // ── Fila sin ID: alta ───────────────────────────────────────────────────────
  if (estadoDestino === 2) {
    return {
      ok: false,
      errores: [
        {
          campo: "Estado",
          motivo:
            'una fila nueva no se puede crear deshabilitada: déjala en "Activo" o quítala del archivo',
        },
      ],
    };
  }

  if (Number(empleado.estado) !== 1) {
    return {
      ok: false,
      errores: [
        { campo: "Empleado", motivo: `"${nombreEmpleado(empleado)}" está dado de baja` },
      ],
    };
  }

  if (Number(reloj.estado) !== 1) {
    return {
      ok: false,
      errores: [{ campo: "Reloj Checador", motivo: `"${etiquetaReloj(reloj)}" está deshabilitado` }],
    };
  }

  const familia =
    catalogos.familiaPorRelojId.get(String(reloj._id)) || new Set([String(reloj._id)]);

  const parDelArchivo = apartados(contexto, "paresUsados");
  const clavePar = `${String(empleado._id)}|${[...familia].sort().join(",")}`;
  if (parDelArchivo.has(clavePar)) {
    return {
      ok: false,
      errores: [
        {
          campo: "Número Empleado",
          motivo: `ese empleado ya viene para ese mismo reloj en la fila ${parDelArchivo.get(clavePar)}`,
        },
      ],
    };
  }
  parDelArchivo.set(clavePar, numeroDeFila(fila));

  const delEmpleado = (catalogos.porEmpleado.get(String(empleado._id)) || []).filter((a) =>
    familia.has(String(a.relojChecadorId)),
  );
  const activa = delEmpleado.find((a) => Number(a.estado) === 1);
  const deshabilitadas = delEmpleado.filter((a) => Number(a.estado) === 2);

  // ¿El usuario que se pide ya lo trae otra asignación activa de la familia?
  if (pinTexto) {
    let ocupante = null;
    for (const relojId of familia) {
      const encontrado = (catalogos.activasPorReloj.get(relojId) || []).find(
        (a) =>
          texto(a.usuarioRelojChecador) === pinTexto &&
          String(a._id) !== String(activa?._id || ""),
      );
      if (encontrado) {
        ocupante = encontrado;
        break;
      }
    }

    if (ocupante) {
      const dueno = catalogos.empleadoPorId.get(String(ocupante.empleadoId));
      const seLibera = (contexto.idsDesactivados || new Set()).has(String(ocupante._id));

      return {
        ok: false,
        errores: [
          {
            campo: "Usuario Reloj",
            motivo: seLibera
              ? `el usuario ${pinTexto} lo libera este mismo archivo (${nombreEmpleado(dueno) || "otra asignación"}), pero no se puede reusar en la misma subida: sube primero las bajas y en una segunda pasada el alta, o dale otro número`
              : `el usuario ${pinTexto} ya lo tiene ${nombreEmpleado(dueno) || "otra asignación"} en ese reloj`,
          },
        ],
      };
    }

    const pinesDelArchivo = apartados(contexto, "pinesUsados");
    const clavePin = `${[...familia].sort().join(",")}|${pinTexto}`;
    if (pinesDelArchivo.has(clavePin)) {
      return {
        ok: false,
        errores: [
          {
            campo: "Usuario Reloj",
            motivo: `el usuario ${pinTexto} ya viene para ese mismo reloj en la fila ${pinesDelArchivo.get(clavePin)}`,
          },
        ],
      };
    }
    pinesDelArchivo.set(clavePin, numeroDeFila(fila));
  }

  if (activa) {
    const pinActivo = texto(activa.usuarioRelojChecador);

    if (!pinTexto || pinTexto === pinActivo) {
      return {
        ok: true,
        accion: "sinCambios",
        idExistente: String(activa._id),
        datos: { ...datos, usuarioRelojChecador: pinActivo, operacion: "sinCambios" },
        valoresAnteriores: { estadoNombre: "Activo" },
      };
    }

    // Ya está activo en ese reloj con otro usuario: se baja el anterior (lo que lo
    // borra del dispositivo) y se da de alta el nuevo.
    return {
      ok: true,
      accion: "editar",
      idExistente: String(activa._id),
      datos: { ...datos, operacion: "reemplazar" },
      cambios: ["usuarioRelojChecador"],
      valoresAnteriores: { usuarioRelojChecador: pinActivo },
    };
  }

  if (deshabilitadas.length > 0) {
    const misma = pinTexto
      ? deshabilitadas.find((a) => texto(a.usuarioRelojChecador) === pinTexto)
      : deshabilitadas.length === 1
        ? deshabilitadas[0]
        : null;

    if (misma) {
      return {
        ok: true,
        accion: "editar",
        idExistente: String(misma._id),
        datos: {
          ...datos,
          usuarioRelojChecador: texto(misma.usuarioRelojChecador),
          operacion: "habilitar",
        },
        cambios: ["estadoNombre"],
        valoresAnteriores: { estadoNombre: "Deshabilitado" },
      };
    }

    if (!pinTexto) {
      return {
        ok: false,
        errores: [
          {
            campo: "Usuario Reloj",
            motivo:
              "el empleado tiene varias asignaciones deshabilitadas en ese reloj; escribe el usuario de la que quieras volver a prender",
          },
        ],
      };
    }

    // Usuario nuevo: se da de alta una asignación nueva. Las deshabilitadas ya
    // perdieron su usuario en el dispositivo, así que no hay nada que borrar.
  }

  return { ok: true, accion: "crear", datos: { ...datos, operacion: "crear" } };
};
